import { Skeleton } from '@mui/material'
import Pill from 'ext-ui/components/Pill/Pill'
import TextAreaInput from 'ext-ui/components/TextAreaInput/TextAreaInput'
import { useAsyncTextInput } from 'ext-ui/hooks/useAsyncTextInput'
import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { useTranslate } from 'src/localization/Trns'
import { TransactionDetailModel } from 'src/models/transaction.models'
import {
  AdditionalFieldsResponse,
  usePatchSingleTransactionAdditionalField,
} from 'src/queries/organizations/additionalFields.queries'
import { useQueryTransactionCustomFields } from 'src/queries/organizations/expenseCategories.queries'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'

import style, {
  skeletonLabel,
  skeletonTitle,
  skeletonValue,
} from './AdditionalFieldsSection.style'

interface AdditionalFieldsInputProps {
  transaction: TransactionDetailModel
  field: AdditionalFieldsResponse
  saveField: (fieldId: string, value: string) => Promise<string | void>
}

const AdditionalFieldsInput: React.FC<AdditionalFieldsInputProps> = ({
  field,
  transaction,
  saveField,
}): React.ReactElement => {
  const translate = useTranslate()

  const [error, setError] = useState<string | undefined>(undefined)

  const currentValue = transaction.additionalFields?.find(
    (txnField) => txnField.expenseAdditionalFieldId === field.id,
  )?.details
  const [initialValue] = useState<string>(currentValue ?? '')

  const state =
    field.required && transaction.isCleared && currentValue === undefined
      ? 'error'
      : undefined

  // Async text input hook for free text handling
  const asyncTextInputProps = useAsyncTextInput({
    value: initialValue,
    onValueChange: async (value: string) => {
      const ret = await saveField(field.id, value)
      if (ret) {
        setError(ret)
      } else {
        setError(undefined)
      }
    },
    debounceDelay: 500,
  })

  return (
    <TextAreaInput
      variant={'medium'}
      state={state}
      label={field.name}
      placeholder={translate('Type here')}
      requirement={field.required ? 'required' : undefined}
      autoResize
      {...asyncTextInputProps}
      errorText={asyncTextInputProps.errorText ?? error}
    />
  )
}

interface AdditionalFieldsSectionProps {
  transaction: TransactionDetailModel
}

const AdditionalFieldsSection: React.FC<AdditionalFieldsSectionProps> = ({
  transaction,
}): React.ReactElement => {
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )

  const { data: mccFilteredFields, isPending: mccFilteredFieldsLoading } =
    useQueryTransactionCustomFields({
      transactionId: transaction.id,
    })

  const additionalFieldsSchema = mccFilteredFields
  const additionalFieldsSchemaLoading = mccFilteredFieldsLoading

  const onSuccessfulSubmit = (fieldId: string, value: string): void => {
    analytics?.track(
      'transaction detail - edit transaction additional fields - button press',
      {
        intent: 'edit transaction additional fields',
        interaction: 'button press',
        screen: calculatedBackgroundLocation.pathname,
        additionalFields: [
          {
            fieldId,
            value: value ?? '',
          },
        ],
      },
    )
  }

  const isMissingRequiredAdditionalFields = useMemo(() => {
    if (!transaction?.isCleared) {
      return false
    }
    const requiredAdditionalFields = additionalFieldsSchema?.filter(
      (field) => field.required,
    )

    return !!requiredAdditionalFields?.some((field) => {
      const fieldDetails = Object.values(
        transaction?.additionalFields || {},
      )?.find(
        (txnFields) => txnFields.expenseAdditionalFieldId === field.id,
      )?.details
      return fieldDetails === undefined
    })
  }, [
    transaction?.isCleared,
    additionalFieldsSchema,
    transaction?.additionalFields,
  ])

  const { mutateAsync: updateAdditionalDetail } =
    usePatchSingleTransactionAdditionalField()

  const saveField = async (
    fieldId: string,
    value: string,
  ): Promise<string | void> => {
    if (!transaction.id) {
      return Promise.reject({
        error: translate(
          'Sorry, something went wrong. Please try again later.',
        ),
      })
    }

    try {
      await updateAdditionalDetail({
        transactionId: transaction.id,
        fieldId,
        body: {
          expenseAdditionalFieldId: fieldId,
          details: value || undefined,
        },
      })
    } catch (e) {
      return translate('Sorry, something went wrong. Please try again later.')
    } finally {
      onSuccessfulSubmit(fieldId, value)
    }
  }

  if (additionalFieldsSchemaLoading) {
    return (
      <div className={'flex-column gap-5'}>
        <Skeleton variant={'rounded'} css={skeletonTitle} />
        {[1, 2, 3].map((_, idx) => (
          <div key={idx} className={'flex-column gap-2'}>
            <Skeleton variant={'rounded'} css={skeletonLabel} />
            <Skeleton variant={'rounded'} css={skeletonValue} />
          </div>
        ))}
      </div>
    )
  }

  if (
    additionalFieldsSchema === undefined ||
    additionalFieldsSchema.length === 0
  ) {
    return <></>
  }

  return (
    <div data-testid={'additional-fields-box'} css={style}>
      <div className={'header'}>
        <span className={'font-h3'}>{translate('Custom Fields')}</span>
        {isMissingRequiredAdditionalFields && (
          <Pill size={'small'} color={'severe'}>
            {translate('Missing')}
          </Pill>
        )}
      </div>
      {additionalFieldsSchema.map((field) => {
        return (
          <AdditionalFieldsInput
            key={`${transaction.id}-${field.id}`}
            field={field}
            transaction={transaction}
            saveField={saveField}
          />
        )
      })}
    </div>
  )
}

export default AdditionalFieldsSection
